import type { IzQuestion } from './izteach.ts';
import { pageParams } from './paging.ts';
import type { ServerQuery } from './paging.ts';

/**
 * Kho đề gốc: every row of the filter except the AI-made ones, paged exactly.
 *
 * The IZ query has no "not equal": `config.msb.source_nin` needs an array the gateway cannot carry
 * (502 on prod) and `_null=false` still reads as $exists true. So the AI rows - few, and read whole
 * once - are paged around instead. When every AI row is newer than the page (the usual case: they are
 * the newest rows of the bank) the page sits right after them; otherwise a window wide enough for the
 * page plus every AI row is read and the page is cut out of it by time.
 */

export const AI_SOURCE = 'ai-quiz-supporter';

export function isAiRow(row: IzQuestion): boolean {
  const config = row.config && typeof row.config === 'object' ? row.config : {};
  const meta = config.msb && typeof config.msb === 'object' ? (config.msb as Record<string, unknown>) : {};
  return meta.source === AI_SOURCE;
}

function time(row: IzQuestion): number {
  return Date.parse(row.updatedAt || '') || 0;
}

/** The page out of a window read from the page's own offset: skip the original rows that sit after AI rows above it. */
export function alignWindow(rows: IzQuestion[], ai: IzQuestion[], size: number): IzQuestion[] {
  if (rows.length === 0) return [];
  const first = time(rows[0]);
  // AI rows newer than the window's first row are above it, so as many original rows were pushed into the window
  const above = ai.filter((row) => time(row) > first).length;
  return rows.filter((row) => !isAiRow(row)).slice(above, above + size);
}

/**
 * One page of Kho đề gốc. `ai` is every AI row matching `filter`; the order is the list's, newest edit first.
 * `limit` is how many rows the page still has when the count is known (paging.ts pageLimit).
 */
export async function fetchOriginalRows(
  fetchRows: (query: ServerQuery) => Promise<IzQuestion[]>,
  filter: ServerQuery,
  page: number,
  size: number,
  ai: IzQuestion[],
  limit: number = size,
): Promise<IzQuestion[]> {
  const paging: ServerQuery = { ...pageParams(page, size), _limit: limit };
  if (ai.length === 0) return fetchRows({ ...filter, ...paging });
  const skip = Number(paging._skip);
  const oldestAi = Math.min.apply(null, ai.map(time));
  const after = await fetchRows({ ...filter, ...paging, _skip: skip + ai.length });
  // older than every AI row means all of them are above it, so exactly `skip` original rows are too
  if (after.length === 0 || time(after[0]) < oldestAi) return after;
  const window = await fetchRows({ ...filter, ...paging, _limit: limit + ai.length });
  return alignWindow(window, ai, limit);
}
